import React, { useEffect, useMemo, useState } from "react";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import Avatar from "../components/Avatar";
import EmployeeFormDialog from "../components/EmployeeFormDialog";
import { useAuth } from "../contexts/AuthContext";
import { usePageAction } from "../contexts/UXChromeContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { useRowOpen } from "../hooks/useRowOpen";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import {
    EMPLOYEE_STATUS_LABEL,
    EMPLOYMENT_BADGE,
    EMPLOYMENT_TYPE_LABEL,
    WORKER_SUBTYPE_LABEL,
    statusBadgeClass,
} from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Plus, Download, Search, Users as UsersIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import useDelayedFlag from "../hooks/useDelayedFlag";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import StaffExtractDrawer from "../components/StaffExtractDrawer";
import ConfirmDialog from "../components/ConfirmDialog";
import ModuleContextChip, { ModuleContextHeader } from "../components/ModuleContextChip";
import EmploymentStatusBadge, {
    EMPLOYMENT_STATUSES,
    EMPLOYMENT_STATUS_LABEL,
} from "../components/EmploymentStatusBadge";
import ChangeStatusDialog from "../components/ChangeStatusDialog";
import UndeployedCandidatesDialog from "../components/UndeployedCandidatesDialog";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { AlertTriangle, UserX } from "lucide-react";
import { hasRole, ROLES } from "../lib/rbac";

const ALL = "__all__";
const PAGE_SIZE = 200;

export default function Staff() {
    const { user } = useAuth();
    const { modules, byId: modulesById } = useModules();
    const scopedId = useScopedModuleId();
    const { moduleCode: pathModuleCode } = useParams();
    const moduleIdForPerm = scopedId || null;
    const canEdit = useHasPermission("employees.write", moduleIdForPerm);
    const canDelete = useHasPermission("employees.delete", moduleIdForPerm);

    const [filters, setFilters] = useState({
        module_id: scopedId || ALL,
        employment_type: ALL,
        status: ALL,
        // Iter60.p59 — Default lifecycle filter to `active` so
        // off-boarded workers are hidden by default. User can pick
        // any status (or ALL) from the dropdown.
        employment_status: "active",
        search: "",
        // Iter60.p20 — Staff-page biometric surface.
        //   biometric_user_id: exact-match text input, debounced 300ms.
        //   has_biometric:     "yes" | "no" | ALL, mapped to bool query param.
        biometric_user_id: "",
        has_biometric: ALL,
        // Iter74 — Bank account chip: `all | has | none`.
        bank_account: "all",
        // Iter74 — Attendance history chip: `all | never | idle | active`.
        // Companion `idle_days` numeric input (default 7, range 1-365) is
        // only meaningful when the chip is `idle` or `active`.
        attendance_history: "all",
        idle_days: 7,
    });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [showDeleted, setShowDeleted] = useState(false);
    const [extractOpen, setExtractOpen] = useState(false);
    const canExtract = useHasPermission("ai.staff_extract", filters.module_id !== ALL ? filters.module_id : null);

    // Bulk-delete selection state (Phase 6b).
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [confirmBulk, setConfirmBulk] = useState(false);
    const [busy, setBusy] = useState(false);

    // Iter60.p59 — Change Status dialog state (row-level + bulk) +
    // Undeployed review modal. `canChangeStatus` gates the buttons —
    // HR/Admin/Manager can flip; Officer/Finance/Viewer cannot.
    const canChangeStatus = hasRole(
        user,
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_DEPT_MGR,
    );
    const [changeStatusFor, setChangeStatusFor] = useState(null); // null | [employees]
    const [undeployedOpen, setUndeployedOpen] = useState(false);

    // Iter59.p7 (Part 2) — Cross-module duplicates.
    // `canSeeCrossDup` gates the "⚠️" pill + modal visibility to management
    // roles only. Viewers/Finance should not see this signal (avoids leaking
    // cross-module identity data to read-only roles).
    const canSeeCrossDup = hasRole(
        user,
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_SENIOR, ROLES.ASL_DEPT_MGR,
    );
    const [crossDupIds, setCrossDupIds] = useState(new Set());
    const [crossDupTotal, setCrossDupTotal] = useState(0);
    const [dupModalFor, setDupModalFor] = useState(null); // {id, name}
    const [dupMatches, setDupMatches] = useState([]);
    const [dupLoading, setDupLoading] = useState(false);

    const toggleOne = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());
    const isAllSelected = data.items.length > 0
        && data.items.filter((e) => !e.deleted_at).every((e) => selectedIds.has(e.id));
    const toggleAllVisible = () => setSelectedIds((prev) => {
        const next = new Set(prev);
        const eligible = data.items.filter((e) => !e.deleted_at);
        const allSelected = eligible.length > 0 && eligible.every((e) => next.has(e.id));
        if (allSelected) eligible.forEach((e) => next.delete(e.id));
        else eligible.forEach((e) => next.add(e.id));
        return next;
    });

    const runBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBusy(true);
        try {
            const { data: resp } = await api.post("/employees/bulk-delete", {
                employee_ids: Array.from(selectedIds),
            });
            if (resp.deleted > 0) {
                toast.success(`${resp.deleted} employee${resp.deleted === 1 ? "" : "s"} deleted. You can restore them via 'Show deleted'.`);
            }
            if (resp.skipped && resp.skipped.length > 0) {
                const reasons = {};
                resp.skipped.forEach((s) => { reasons[s.reason] = (reasons[s.reason] || 0) + 1; });
                const summary = Object.entries(reasons)
                    .map(([r, n]) => `${n} ${r.replace(/_/g, " ")}`).join(", ");
                toast.warning(`${resp.skipped.length} skipped: ${summary}.`);
            }
            clearSelection();
            setConfirmBulk(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    usePageAction(
        canEdit
            ? { label: "Add Employee", run: () => { setEditing(null); setDialogOpen(true); } }
            : null,
        [canEdit],
    );

    const load = async () => {
        setLoading(true);
        const params = { page, page_size: PAGE_SIZE };
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        if (filters.employment_type !== ALL)
            params.employment_type = filters.employment_type;
        if (filters.status !== ALL) params.status = filters.status;
        // Iter60.p59 — Lifecycle filter passes through as
        // `employment_status`; backend transparently matches legacy
        // rows lacking the field when the filter is `active`.
        if (filters.employment_status !== ALL)
            params.employment_status = filters.employment_status;
        if (filters.search.trim()) params.search = filters.search.trim();
        // Iter60.p20 — biometric filters. Exact-match takes precedence over
        // the has/hasn't chip on the backend, but we send both for clarity.
        if (debouncedBioId) params.biometric_user_id = debouncedBioId;
        if (filters.has_biometric !== ALL) params.has_biometric = filters.has_biometric === "yes";
        // Iter74 — Bank + Attendance filters. Omitted when "all" so the URL
        // stays clean and the backend applies no additional predicate.
        if (filters.bank_account !== "all") params.bank_account = filters.bank_account;
        if (filters.attendance_history !== "all") {
            params.attendance_history = filters.attendance_history;
            // idle_days is only relevant when partitioning by idle/active; the
            // backend defaults it to 7 anyway, but sending it makes the URL
            // self-describing for shared bookmarks.
            if (filters.attendance_history === "idle" || filters.attendance_history === "active") {
                params.idle_days = filters.idle_days;
            }
        }
        if (showDeleted) params.include_deleted = true;
        try {
            const { data } = await api.get("/employees", { params });
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    // Iter52d — Fetch effective rates for the scoped module and hydrate a rate map
    // used by the PAY RATE column. Works for any module (IC / AP / CN).
    const [rateMap, setRateMap] = useState({});
    const hasScopedModule = !!scopedId;
    useEffect(() => {
        if (!hasScopedModule) { setRateMap({}); return; }
        (async () => {
            try {
                const { data } = await api.get(`/wage-rates/effective-rates/by-module/${scopedId}`);
                setRateMap(data.rates || {});
            } catch { setRateMap({}); }
        })();
    }, [hasScopedModule, scopedId, data]);

    // Iter60.p20 — Debounce the biometric-ID search input (300ms) so we don't
    // hammer the backend on every keystroke. Load fires when the debounced
    // value changes, in the same useEffect as the other filter changes.
    const [debouncedBioId, setDebouncedBioId] = useState("");
    useEffect(() => {
        const h = setTimeout(
            () => setDebouncedBioId(filters.biometric_user_id.trim()),
            300,
        );
        return () => clearTimeout(h);
    }, [filters.biometric_user_id]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        load();
    }, [page, filters.module_id, filters.employment_type, filters.status, filters.employment_status, filters.has_biometric, filters.bank_account, filters.attendance_history, filters.idle_days, debouncedBioId, showDeleted]);

    // Iter74 — Two-way URL sync for the Iter74 filter trio (bank_account,
    // attendance_history, idle_days). Kept separate from the other on-page
    // filters (which don't currently sync) so a shared /staff URL restores
    // the caller's bank + attendance triage view exactly.
    useFilterUrlSync({
        filters,
        defaults: { bank_account: "all", attendance_history: "all", idle_days: 7 },
        paramMap: {
            bank_account: "bank_account",
            attendance_history: "attendance_history",
            idle_days: {
                param: "idle_days",
                // Serialize numeric → string, deserialize back → int with
                // range clamp (mirrors backend ge=1, le=365).
                toUrl: (v) => String(v ?? 7),
                fromUrl: (raw) => {
                    const n = parseInt(raw, 10);
                    if (!Number.isFinite(n)) return 7;
                    return Math.max(1, Math.min(365, n));
                },
            },
        },
        hydrate: (patch) => setFilters((f) => ({ ...f, ...patch })),
    });

    // Iter59.p7 (Part 2) — Fetch the set of employee IDs that participate in
    // at least one cross-module duplicate group. Refreshed on module/scope
    // change and after list `load()`. Silent-failure — the pill just won't
    // render if the endpoint is unavailable.
    useEffect(() => {
        if (!canSeeCrossDup) { setCrossDupIds(new Set()); setCrossDupTotal(0); return; }
        (async () => {
            try {
                const { data } = await api.get("/employees/cross-module-duplicate-count");
                setCrossDupIds(new Set(data.duplicate_employee_ids || []));
                setCrossDupTotal(data.employees_with_cross_module_duplicates || 0);
            } catch {
                setCrossDupIds(new Set());
                setCrossDupTotal(0);
            }
        })();
    }, [canSeeCrossDup, data]);

    const openDupModal = async (emp) => {
        setDupModalFor({ id: emp.id, name: emp.full_name });
        setDupMatches([]);
        setDupLoading(true);
        try {
            const { data } = await api.get(`/employees/${emp.id}/cross-module-duplicates`);
            setDupMatches(data.matches || []);
        } catch (err) {
            toast.error(formatApiError(err));
            setDupMatches([]);
        } finally {
            setDupLoading(false);
        }
    };

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        load();
    };

    const exportCsv = async () => {
        const params = {};
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        if (filters.employment_type !== ALL)
            params.employment_type = filters.employment_type;
        if (filters.status !== ALL) params.status = filters.status;
        if (filters.search.trim()) params.search = filters.search.trim();
        // Iter74 — Mirror the on-screen bank + attendance filters into the
        // CSV so exported files match the visible triage list exactly.
        if (filters.bank_account !== "all") params.bank_account = filters.bank_account;
        if (filters.attendance_history !== "all") {
            params.attendance_history = filters.attendance_history;
            if (filters.attendance_history === "idle" || filters.attendance_history === "active") {
                params.idle_days = filters.idle_days;
            }
        }
        try {
            const resp = await api.get("/employees/export.csv", {
                params,
                responseType: "blob",
            });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const disposition = resp.headers?.["content-disposition"] || "";
            const m = disposition.match(/filename="?([^";]+)"?/);
            a.download =
                m?.[1] ||
                `employees_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV exported");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
    const showSkeleton = useDelayedFlag(loading, 200);
    const filtered = filters.module_id !== ALL || filters.employment_type !== ALL || filters.status !== ALL || filters.employment_status !== "active" || filters.search.trim() !== "" || filters.bank_account !== "all" || filters.attendance_history !== "all";
    const clearFilters = () => {
        setFilters({ module_id: scopedId || ALL, employment_type: ALL, status: ALL, employment_status: "active", search: "", biometric_user_id: "", has_biometric: ALL, bank_account: "all", attendance_history: "all", idle_days: 7 });
        setPage(1);
    };
    const moduleName = filters.module_id !== ALL ? modulesById[filters.module_id]?.name : "all modules";

    return (
        <AppShell title="Staff Directory">
            <EmployeeFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                employee={editing}
                onSaved={load}
            />

            <section className="space-y-5" data-testid="staff-page">
                {/* Iter60.p13 — Prominent module context indicator on Staff */}
                {scopedId && (
                    <ModuleContextHeader
                        moduleId={scopedId}
                        subtitle="Staff shown are scoped to this module only."
                        testid="staff-module-context-header"
                    />
                )}
                {/* Filters & actions */}
                <div className="asl-card p-4">
                    <div className="flex flex-wrap items-end gap-3 justify-between">
                        <div className="flex flex-wrap items-end gap-3">
                            <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
                                <div className="relative">
                                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--asl-muted)]" />
                                    <Input
                                        placeholder="Search name, phone, module ID…"
                                        value={filters.search}
                                        onChange={(e) =>
                                            setFilters((f) => ({
                                                ...f,
                                                search: e.target.value,
                                            }))
                                        }
                                        className="pl-9 w-72"
                                        data-testid="staff-search-input"
                                    />
                                </div>
                                <Button type="submit" variant="outline" size="sm" data-testid="staff-search-button">
                                    Search
                                </Button>
                            </form>

                            {/* Iter60.p13 — Module filter. Inside a module
                                workspace we hide the dropdown (which would
                                otherwise show "All modules" + other modules)
                                and show a locked chip; ModuleContextHeader
                                above already announces the current module. */}
                            {scopedId ? (
                                <ModuleContextChip
                                    moduleId={scopedId}
                                    testid="staff-module-chip"
                                />
                            ) : (
                                <FilterSelect
                                    label="Module"
                                    value={filters.module_id}
                                    onValueChange={(v) => {
                                        setFilters((f) => ({ ...f, module_id: v }));
                                        setPage(1);
                                    }}
                                    items={[
                                        { value: ALL, label: "All modules" },
                                        ...modules.map((m) => ({ value: m.id, label: m.name })),
                                    ]}
                                    testid="staff-filter-module"
                                />
                            )}
                            <FilterSelect
                                label="Type"
                                value={filters.employment_type}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, employment_type: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: ALL, label: "All types" },
                                    { value: "casual", label: "Casual" },
                                    { value: "non_casual", label: "Non-Casual" },
                                ]}
                                testid="staff-filter-type"
                            />
                            <FilterSelect
                                label="Status"
                                value={filters.status}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, status: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: ALL, label: "All statuses" },
                                    { value: "active", label: "Active" },
                                    { value: "suspended", label: "Suspended" },
                                    { value: "resigned", label: "Resigned" },
                                    { value: "terminated", label: "Terminated" },
                                    { value: "replaced", label: "Replaced" },
                                ]}
                                testid="staff-filter-status"
                            />
                            {/* Iter60.p59 — Employment lifecycle filter chip.
                                Defaults to `active` on mount; pick `ALL` to
                                see off-boarded workers alongside active. */}
                            <FilterSelect
                                label="Employment"
                                value={filters.employment_status}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, employment_status: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: ALL, label: "All lifecycle" },
                                    ...EMPLOYMENT_STATUSES.map((s) => ({
                                        value: s,
                                        label: EMPLOYMENT_STATUS_LABEL[s],
                                    })),
                                ]}
                                testid="staff-filter-employment-status"
                            />
                            {/* Iter60.p20 — Biometric enrollment filter chip. */}
                            <FilterSelect
                                label="Biometric"
                                value={filters.has_biometric}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, has_biometric: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: ALL, label: "All" },
                                    { value: "yes", label: "Enrolled" },
                                    { value: "no", label: "Not Enrolled" },
                                ]}
                                testid="staff-filter-has-biometric"
                            />
                            {/* Iter74 — Bank Account triage chip. Green-vs-grey
                                indicator on each row still shows the raw state;
                                this dropdown narrows the visible set so payroll
                                admins can drive to zero unbanked workers. */}
                            <FilterSelect
                                label="Bank Account"
                                value={filters.bank_account}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, bank_account: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: "all", label: "All" },
                                    { value: "has", label: "Has bank account" },
                                    { value: "none", label: "No bank account" },
                                ]}
                                testid="staff-filter-bank-account"
                            />
                            {/* Iter74 — Attendance history triage chip. `idle`
                                and `active` partition the "ever clocked in"
                                cohort by `idle_days` (default 7); `never`
                                surfaces workers with no lifetime clock-in. */}
                            <FilterSelect
                                label="Attendance History"
                                value={filters.attendance_history}
                                onValueChange={(v) => {
                                    setFilters((f) => ({ ...f, attendance_history: v }));
                                    setPage(1);
                                }}
                                items={[
                                    { value: "all", label: "All" },
                                    { value: "never", label: "Never clocked in" },
                                    { value: "idle", label: "Idle N+ days" },
                                    { value: "active", label: "Active recently" },
                                ]}
                                testid="staff-filter-attendance-history"
                            />
                            {/* Iter74 — Numeric N for the idle/active partition.
                                Hidden when the chip is `all` or `never` (N is
                                irrelevant in those buckets). Range 1-365 mirrors
                                the backend `Query(ge=1, le=365)` clamp. */}
                            {(filters.attendance_history === "idle" || filters.attendance_history === "active") && (
                                <div className="space-y-1">
                                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                        Idle Days (N)
                                    </div>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={filters.idle_days}
                                        onChange={(e) => {
                                            const raw = parseInt(e.target.value, 10);
                                            const n = Number.isFinite(raw) ? Math.max(1, Math.min(365, raw)) : 7;
                                            setFilters((f) => ({ ...f, idle_days: n }));
                                            setPage(1);
                                        }}
                                        className="w-24"
                                        data-testid="staff-filter-idle-days"
                                        aria-label="Idle days threshold"
                                    />
                                </div>
                            )}
                            {/* Iter60.p20 — Exact-match biometric ID search
                                (debounced 300ms). Kept separate from the
                                fuzzy name/code/phone search so admins can
                                answer "who is #1219?" with a single field. */}
                            <div className="relative">
                                <Input
                                    placeholder="Search Biometric ID…"
                                    value={filters.biometric_user_id}
                                    onChange={(e) =>
                                        setFilters((f) => ({
                                            ...f,
                                            biometric_user_id: e.target.value,
                                        }))
                                    }
                                    className="w-48"
                                    data-testid="staff-search-biometric-id"
                                />
                                {filters.biometric_user_id && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFilters((f) => ({ ...f, biometric_user_id: "" }))
                                        }
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[color:var(--asl-muted)] hover:text-[color:var(--asl-navy)]"
                                        aria-label="Clear biometric ID search"
                                        data-testid="staff-search-biometric-clear"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {canSeeCrossDup && crossDupTotal > 0 && (
                                <span
                                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                                    title="Employees with matching name or Module ID in another module. Click a row's ⚠️ icon to see matches."
                                    data-testid="staff-cross-dup-pill"
                                >
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    {crossDupTotal} cross-module {crossDupTotal === 1 ? "duplicate" : "duplicates"}
                                </span>
                            )}
                            <ShowDeletedToggle value={showDeleted} onChange={(v) => { setShowDeleted(v); setPage(1); }} testid="staff-show-deleted" />
                            {/* Iter60.p59 — Suggest Undeployed quick action.
                                Opens a review modal listing every active
                                worker with zero lifetime biometric events. */}
                            {canChangeStatus && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setUndeployedOpen(true)}
                                    data-testid="staff-suggest-undeployed"
                                >
                                    <UserX className="h-4 w-4 mr-1" /> Suggest Undeployed
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={exportCsv}
                                data-testid="staff-export-csv"
                            >
                                <Download className="h-4 w-4 mr-1" /> Export CSV
                            </Button>
                            {canExtract && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setExtractOpen(true)}
                                    data-testid="staff-ai-extract-btn"
                                    className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-rose-50"
                                >
                                    ✨ Extract from Document
                                </Button>
                            )}
                            {canEdit && (
                                <Button
                                    size="sm"
                                    className="asl-navy-bg text-white"
                                    onClick={() => {
                                        setEditing(null);
                                        setDialogOpen(true);
                                    }}
                                    data-testid="staff-add-employee"
                                >
                                    <Plus className="h-4 w-4 mr-1" /> Add Employee
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bulk selection action bar (Phase 6b) */}
                {selectedIds.size > 0 && canDelete && (
                    <div
                        className="sticky top-2 z-20 flex items-center justify-between px-4 py-2 rounded-lg shadow-md"
                        style={{ background: "var(--asl-navy-2)", color: "white" }}
                        data-testid="staff-bulk-bar"
                    >
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-white/15" data-testid="staff-bulk-count">
                                {selectedIds.size}
                            </span>
                            <span className="text-sm font-semibold">
                                {selectedIds.size === 1 ? "employee selected" : "employees selected"}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearSelection}
                                className="text-white hover:bg-white/10"
                                data-testid="staff-bulk-clear"
                            >
                                Clear selection
                            </Button>
                            {/* Iter60.p59 — Bulk Change Status. Opens the
                                same dialog as the row-level kebab but pre-
                                populated with every selected employee. */}
                            {canChangeStatus && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setChangeStatusFor(
                                        data.items.filter((e) => selectedIds.has(e.id) && !e.deleted_at)
                                    )}
                                    className="border-white/40 text-white hover:bg-white/10"
                                    data-testid="staff-bulk-change-status"
                                >
                                    Change Status…
                                </Button>
                            )}
                            <Button
                                size="sm"
                                onClick={() => setConfirmBulk(true)}
                                className="bg-[color:var(--asl-danger)] hover:bg-red-700 text-white"
                                data-testid="staff-bulk-delete"
                            >
                                <Trash2 className="h-4 w-4 mr-1" /> Delete selected
                            </Button>
                        </div>
                    </div>
                )}

                {/* Iter74.6 — "Never clocked in" CTA banner. Only shown
                    when the Attendance History chip is `never` AND the
                    caller can flip employment status (Admin, HR Manager,
                    HR Officer, Dept Manager). Reuses the existing
                    UndeployedCandidatesDialog so this is a wiring change
                    only — no new logic. Hidden gracefully for roles
                    without the permission. */}
                {filters.attendance_history === "never" && canChangeStatus && data.total > 0 && (
                    <div
                        className="mb-3 rounded border px-4 py-3 flex items-center gap-4 flex-wrap"
                        style={{
                            borderColor: "var(--asl-crimson)",
                            background: "rgba(168, 50, 74, 0.06)",
                        }}
                        data-testid="staff-never-clockin-cta"
                    >
                        <UserX
                            className="h-5 w-5 flex-shrink-0"
                            style={{ color: "var(--asl-crimson)" }}
                            aria-hidden="true"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold asl-navy-text" data-testid="staff-never-clockin-cta-title">
                                {data.total} {data.total === 1 ? "employee has" : "employees have"} never clocked in
                            </div>
                            <div className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                Review the list and mark the workers who never joined biometric as
                                {" "}
                                <span className="font-medium">Undeployed</span>.
                            </div>
                        </div>
                        <Button
                            size="sm"
                            className="asl-navy-bg text-white"
                            onClick={() => setUndeployedOpen(true)}
                            data-testid="staff-never-clockin-cta-btn"
                        >
                            <UserX className="h-4 w-4 mr-1" /> Mark as Undeployed…
                        </Button>
                    </div>
                )}

                {/* Table */}
                <div className="asl-card overflow-hidden">
                    {loading ? (
                        showSkeleton ? <TableSkeleton rows={6} cols={12} testid="staff-loading" /> : null
                    ) : data.items.length === 0 ? (
                        <EmptyState
                            icon={UsersIcon}
                            title={filtered ? "Nothing matches these filters. Try widening the range or clearing a filter." : `No employees in ${moduleName} yet`}
                            description={filtered ? "Try widening filters or clearing them." : "Add your first employee to start tracking attendance, contracts, and payroll."}
                            ctaLabel={canEdit && !filtered ? "Add Employee" : undefined}
                            onCta={canEdit && !filtered ? () => { setEditing(null); setDialogOpen(true); } : undefined}
                            filtered={filtered}
                            onClear={clearFilters}
                            testid="staff-empty"
                        />
                    ) : (
                        <table className="asl-table asl-table--zebra" data-testid="staff-table">
                            <thead
                                className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{
                                    background: "var(--asl-navy-50)",
                                    borderBottom: "1px solid var(--asl-border)",
                                }}
                            >
                                <tr>
                                    <th className="px-3 py-3 w-10">
                                        {canDelete && (
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={toggleAllVisible}
                                                aria-label="Select all"
                                                data-testid="staff-select-all"
                                                className="h-4 w-4 cursor-pointer accent-[color:var(--asl-navy-2)]"
                                            />
                                        )}
                                    </th>
                                    <th className="text-left px-4 py-3 w-16"></th>
                                    {/* Iter60.p15 — Module ID is now the PRIMARY identifier
                                        column (front-of-row after avatar). EMP Code stays
                                        available but demoted to secondary emphasis. */}
                                    <th className="text-left px-4 py-3" data-testid="staff-th-module-id">Module ID</th>
                                    {/* Iter60.p49 Item B — EMP Code column removed
                                        from the visible header set (kept in DB for internal joins). */}
                                    <th className="text-left px-4 py-3">Name</th>
                                    <th className="text-left px-4 py-3">Module</th>
                                    <th className="text-left px-4 py-3">Job Title</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                    {/* Iter60.p59 — Employment lifecycle
                                        column. Coloured badge with the same
                                        taxonomy used in the Change Status
                                        dialog + Attendance grid. */}
                                    <th
                                        className="text-left px-4 py-3"
                                        data-testid="staff-th-employment-status"
                                    >
                                        Employment
                                    </th>
                                    {hasScopedModule && <th className="text-left px-4 py-3" data-testid="staff-th-pay-rate">Pay Rate</th>}
                                    <th className="text-left px-4 py-3" data-testid="staff-th-biometric-id">Biometric ID</th>
                                    {/* Iter74 — Days since last clock-in. Visible by
                                        default so idle workers pop out during triage. */}
                                    <th
                                        className="text-left px-4 py-3"
                                        data-testid="staff-th-days-since-clock-in"
                                        title="Days since the employee's most recent clock-in (Africa/Mogadishu)."
                                    >
                                        Days idle
                                    </th>
                                    <th className="text-left px-4 py-3">Phone</th>
                                    <th className="text-right px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((e) => (
                                    <StaffRow
                                        key={e.id}
                                        e={e}
                                        moduleCode={pathModuleCode}
                                        modulesById={modulesById}
                                        canEdit={canEdit}
                                        canDelete={canDelete}
                                        selected={selectedIds.has(e.id)}
                                        onToggleSelect={() => toggleOne(e.id)}
                                        onEdit={() => { setEditing(e); setDialogOpen(true); }}
                                        onChange={load}
                                        onChangeStatus={
                                            canChangeStatus
                                                ? () => setChangeStatusFor([e])
                                                : null
                                        }
                                        isICScope={hasScopedModule}
                                        rateMap={rateMap}
                                        hasCrossDup={canSeeCrossDup && crossDupIds.has(e.id)}
                                        onOpenDup={() => openDupModal(e)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!loading && data.items.length > 0 && (
                        <div
                            className="flex items-center justify-between px-5 py-3 text-xs text-[color:var(--asl-muted)]"
                            style={{ borderTop: "1px solid var(--asl-border)" }}
                        >
                            <span data-testid="staff-pagination-info">
                                Showing {(page - 1) * PAGE_SIZE + 1}–
                                {Math.min(page * PAGE_SIZE, data.total)} of {data.total}
                            </span>
                            {data.total > PAGE_SIZE && (
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => p - 1)}
                                        data-testid="staff-prev-page"
                                    >
                                        Prev
                                    </Button>
                                    <span className="px-2 mono">
                                        {page} / {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                        data-testid="staff-next-page"
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
            <StaffExtractDrawer
                open={extractOpen}
                onOpenChange={setExtractOpen}
                moduleId={filters.module_id !== ALL ? filters.module_id : null}
                onCreated={() => load()}
            />
            {/* Iter60.p59 — Change Status + Suggest Undeployed dialogs.
                Both re-load the Staff list on success so the badge
                update is immediate. */}
            <ChangeStatusDialog
                open={!!changeStatusFor}
                onOpenChange={(open) => !open && setChangeStatusFor(null)}
                employees={changeStatusFor || []}
                currentStatus={changeStatusFor?.[0]?.employment_status}
                onSaved={() => { setSelectedIds(new Set()); load(); }}
            />
            <UndeployedCandidatesDialog
                open={undeployedOpen}
                onOpenChange={setUndeployedOpen}
                moduleId={filters.module_id !== ALL ? filters.module_id : null}
                moduleCode={pathModuleCode}
                onSaved={() => load()}
            />
            <ConfirmDialog
                open={confirmBulk}
                onOpenChange={setConfirmBulk}
                variant="destructive"
                title={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? "employee" : "employees"}?`}
                description="These employees will be marked as deleted and hidden from the directory. Their existing payroll, attendance, contracts, discipline records, and PPE history will be preserved and remain accessible for audits. You can restore them later via the 'Show deleted' toggle."
                confirmLabel={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? "employee" : "employees"}`}
                cancelLabel="Cancel"
                onConfirm={runBulkDelete}
                busy={busy}
                testid="staff-bulk-confirm"
            />

            {/* Iter59.p7 (Part 2) — Cross-module duplicates modal. Lists every
                other-module employee that matches by full_name or module ID.
                Non-blocking, informational only. */}
            <Dialog open={!!dupModalFor} onOpenChange={(o) => !o && setDupModalFor(null)}>
                <DialogContent className="max-w-2xl" data-testid="staff-cross-dup-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            <span className="inline-flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                Cross-module duplicates — {dupModalFor?.name}
                            </span>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="pt-2 text-sm">
                        <p className="text-[color:var(--asl-muted)] mb-3">
                            The following employees in other modules share the same full name
                            or Module ID. This is informational only — cross-module duplicates
                            are allowed (same person may work across sites).
                        </p>
                        {dupLoading ? (
                            <div className="py-6 text-center text-[color:var(--asl-muted)]" data-testid="staff-cross-dup-loading">Loading…</div>
                        ) : dupMatches.length === 0 ? (
                            <div className="py-6 text-center text-[color:var(--asl-muted)]" data-testid="staff-cross-dup-empty">
                                No cross-module duplicates found.
                            </div>
                        ) : (
                            <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--asl-border)" }}>
                                <table className="asl-table asl-table--zebra w-full" data-testid="staff-cross-dup-table">
                                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]" style={{ background: "var(--asl-navy-50)" }}>
                                        <tr>
                                            <th className="text-left px-3 py-2">Module</th>
                                            <th className="text-left px-3 py-2">Module ID</th>
                                            <th className="text-left px-3 py-2">Name</th>
                                            <th className="text-left px-3 py-2">Job Title</th>
                                            <th className="text-left px-3 py-2">Matched on</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dupMatches.map((m) => (
                                            <tr key={m.id} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`staff-cross-dup-match-${m.employee_code}`}>
                                                <td className="px-3 py-2 text-xs">{m.module_name || m.module_code || "—"}</td>
                                                <td className="px-3 py-2 mono text-xs asl-crimson-text font-semibold">{m.module_employee_id || "—"}</td>
                                                <td className="px-3 py-2 font-medium">{m.full_name}</td>
                                                <td className="px-3 py-2 text-xs">{m.job_title || "—"}</td>
                                                <td className="px-3 py-2 text-xs">
                                                    {(m.matched_on || []).map((f) => (
                                                        <span key={f} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold">
                                                            {f === "full_name" ? "Name" : "Module ID"}
                                                        </span>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDupModalFor(null)} data-testid="staff-cross-dup-close">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}

function FilterSelect({ label, value, onValueChange, items, testid }) {
    return (
        <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                {label}
            </div>
            <Select value={value} onValueChange={onValueChange}>
                <SelectTrigger className="w-44" data-testid={testid}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {items.map((i) => (
                        <SelectItem key={i.value} value={i.value} data-testid={`${testid}-opt-${i.value}`}>
                            {i.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}


function StaffRow({ e, moduleCode, modulesById, canEdit, canDelete, selected, onToggleSelect, onEdit, onChange, onChangeStatus, isICScope, rateMap, hasCrossDup, onOpenDup }) {
    const rowProps = useRowOpen(`/m/${moduleCode}/staff/${e.id}`, { testid: `staff-row-${e.employee_code}` });
    return (
        <tr
            {...rowProps}
            className={`border-t ${rowProps.className} ${selected ? "bg-[color:var(--asl-navy-50)]" : ""}`}
            style={{ borderColor: "var(--asl-border)" }}
        >
            <td className="px-3 py-3 w-10" data-no-row-click>
                {canDelete && !e.deleted_at && (
                    <input
                        type="checkbox"
                        checked={selected || false}
                        onChange={onToggleSelect}
                        onClick={(ev) => ev.stopPropagation()}
                        aria-label={`Select ${e.full_name}`}
                        data-testid={`staff-select-${e.employee_code}`}
                        className="h-4 w-4 cursor-pointer accent-[color:var(--asl-navy-2)]"
                    />
                )}
            </td>
            <td className="px-4 py-3">
                <Avatar src={e.photo_url} name={e.full_name} size={36} />
            </td>
            {/* Iter60.p15 — Module ID becomes the primary identifier: larger,
                bolder, navy text. Kept mono so it's clearly a code, not a
                name. Falls back to "—" when unset, in a muted tone. */}
            <td
                className="px-4 py-3 mono font-bold asl-navy-text text-sm"
                style={{
                    color: e.module_employee_id
                        ? "var(--asl-navy-700, #1B2A56)"
                        : "var(--asl-text-3)",
                }}
                data-testid={`staff-module-id-${e.employee_code}`}
                data-module-id={e.module_employee_id || ""}
            >
                <DeletedRowDecorator deleted={!!e.deleted_at}>
                    {e.module_employee_id || "—"}
                </DeletedRowDecorator>
            </td>
            {/* Iter60.p49 Item B — EMP Code cell removed. Module ID is the
                sole visible identifier on the Staff row. */}
            <td className="px-4 py-3 font-medium asl-navy-text">
                <DeletedRowDecorator deleted={!!e.deleted_at}>{e.full_name}</DeletedRowDecorator>
                {e.deleted_at && <DeletedPill deletedAt={e.deleted_at} />}
                {/* Iter60.p12 — small $ indicator: green = bank details set,
                    grey = missing. Hover-title explains which. */}
                <span
                    className={
                        "ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-bold "
                        + (e.has_bank_details
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                            : "bg-slate-100 text-slate-400 border border-slate-200")
                    }
                    title={e.has_bank_details ? "Bank details on file" : "No bank details on file"}
                    data-testid={`staff-bank-indicator-${e.employee_code}`}
                    data-bank-set={e.has_bank_details ? "true" : "false"}
                    aria-label={e.has_bank_details ? "Bank details on file" : "No bank details on file"}
                >$</span>
                {hasCrossDup && (
                    <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); onOpenDup(); }}
                        className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
                        title="Same name or Module ID exists in another module. Click to view matches."
                        data-testid={`staff-cross-dup-${e.employee_code}`}
                        data-no-row-click
                    >
                        <AlertTriangle className="h-3 w-3" /> Dup
                    </button>
                )}
            </td>
            <td className="px-4 py-3 text-xs">
                {modulesById[e.module_id]?.name || "—"}
            </td>
            <td className="px-4 py-3 text-xs">{e.job_title}</td>
            <td className="px-4 py-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${EMPLOYMENT_BADGE[e.employment_type]}`}>
                    {EMPLOYMENT_TYPE_LABEL[e.employment_type]}
                    {e.worker_subtype ? ` · ${WORKER_SUBTYPE_LABEL[e.worker_subtype]}` : ""}
                </span>
            </td>
            <td className="px-4 py-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(e.status)}`}>
                    {EMPLOYEE_STATUS_LABEL[e.status]}
                </span>
            </td>
            {/* Iter60.p59 — Employment lifecycle badge column. */}
            <td className="px-4 py-3" data-testid={`staff-employment-status-${e.id}`}>
                <EmploymentStatusBadge
                    status={e.employment_status || "active"}
                    testid={`staff-employment-badge-${e.id}`}
                />
            </td>
            {isICScope && (
                <td className="px-4 py-3 text-xs" data-testid={`staff-pay-${e.employee_code}`}>
                    {(() => {
                        const r = rateMap[e.id];
                        if (!r || r.amount == null) {
                            // Iter59.p4 — clearer empty state + admin quick-action
                            // to open the Wage Rates page with a prefilled New
                            // Wage Rate dialog for this employee.
                            const prefillHref = `/m/${moduleCode.toLowerCase()}/wage-rates`
                                + `?prefill_employee=${encodeURIComponent(e.id)}`
                                + `&prefill_job_title=${encodeURIComponent(r?.job_title || e.job_title || "")}`
                                + `&prefill_worker_category=${encodeURIComponent(r?.worker_category || e.worker_category || "")}`;
                            return (
                                <span className="inline-flex items-center gap-2">
                                    <span className="italic text-[color:var(--asl-muted)]" data-testid={`staff-pay-empty-${e.employee_code}`}>— no rate set —</span>
                                    {canEdit && (
                                        <Link to={prefillHref}
                                            className="text-[10px] text-blue-600 hover:underline"
                                            data-testid={`staff-set-rate-${e.employee_code}`}
                                            onClick={(ev) => ev.stopPropagation()}>
                                            + Set rate
                                        </Link>
                                    )}
                                </span>
                            );
                        }
                        const dotColor = r.source === "employee_specific" ? "bg-emerald-500" : "bg-blue-500";
                        const dotTitle = r.source === "employee_specific"
                            ? "Rate set specifically for this person"
                            : "Inherited from role default";
                        return (
                            <span className="inline-flex items-center gap-1.5 mono">
                                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} title={dotTitle} />
                                <span>${Number(r.amount).toFixed(2)} <span className="text-[color:var(--asl-muted)]">/ {r.frequency}</span></span>
                            </span>
                        );
                    })()}
                </td>
            )}
            {/* Iter60.p20 — Biometric ID column. Enrolled → mono badge with
                emerald dot; unenrolled → em-dash + "Link…" ghost button that
                opens the existing edit dialog focused on the biometric field. */}
            <td className="px-4 py-3" data-no-row-click>
                {e.biometric_user_id ? (
                    <span
                        className="inline-flex items-center gap-1.5 mono text-xs"
                        title={
                            (e.biometric_enrollments || []).length > 0
                                ? `${(e.biometric_enrollments || []).length} device enrollment${(e.biometric_enrollments || []).length === 1 ? "" : "s"}`
                                : "Enrolled"
                        }
                        data-testid={`staff-biometric-id-${e.id}`}
                    >
                        <span
                            className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                            aria-hidden="true"
                        />
                        <span className="font-semibold asl-navy-text">#{e.biometric_user_id}</span>
                        {(e.biometric_enrollments || []).length > 0 && (
                            <span className="text-[10px] text-[color:var(--asl-muted)]">
                                · {(e.biometric_enrollments || []).length}
                            </span>
                        )}
                    </span>
                ) : (
                    <span
                        className="inline-flex items-center gap-2 text-xs text-[color:var(--asl-muted)]"
                        data-testid={`staff-biometric-id-${e.id}`}
                    >
                        <span>—</span>
                        {canEdit && !e.deleted_at && (
                            <button
                                type="button"
                                onClick={onEdit}
                                className="text-[color:var(--asl-crimson)] hover:underline text-[11px] font-medium"
                                data-testid={`staff-biometric-link-${e.id}`}
                            >
                                Link…
                            </button>
                        )}
                    </span>
                )}
            </td>
            {/* Iter74 — Days since last clock-in. `—` for never clocked in.
                Integer >= 0 otherwise (Mogadishu local calendar). Emerald
                on 0-6, amber on 7-29, crimson on 30+ so idle risk is visible
                at a glance without opening the profile. */}
            <td
                className="px-4 py-3 text-xs mono"
                data-testid={`staff-days-idle-${e.id}`}
                data-days-since={e.days_since_last_clock_in ?? ""}
            >
                {(() => {
                    const n = e.days_since_last_clock_in;
                    if (n == null) {
                        return <span className="text-[color:var(--asl-muted)]">—</span>;
                    }
                    let cls = "text-emerald-700";
                    if (n >= 30) cls = "text-rose-700 font-semibold";
                    else if (n >= 7) cls = "text-amber-700 font-semibold";
                    return <span className={cls}>{n}</span>;
                })()}
            </td>
            <td className="px-4 py-3 mono text-xs">{e.phone}</td>
            <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1" data-no-row-click>
                    <Link to={`/m/${moduleCode}/staff/${e.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`staff-view-${e.employee_code}`}>View</Button>
                    </Link>
                    {canEdit && !e.deleted_at && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onEdit}
                            data-testid={`staff-edit-${e.employee_code}`}
                        >
                            Edit
                        </Button>
                    )}
                    {/* Iter60.p59 — Row-level "Change Status" trigger.
                        Only rendered when the caller passed a handler
                        (role gated at the parent). */}
                    {onChangeStatus && !e.deleted_at && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onChangeStatus}
                            data-testid={`staff-change-status-${e.id}`}
                            title="Change employment status (resigned, terminated, undeployed, …)"
                        >
                            Status
                        </Button>
                    )}
                    <RowActions
                        entity="employees"
                        endpoint="/employees"
                        id={e.id}
                        label={e.employee_code}
                        deleted={!!e.deleted_at}
                        moduleId={e.module_id}
                        onChange={onChange}
                        testid={`staff-row-actions-${e.employee_code}`}
                    />
                </div>
            </td>
        </tr>
    );
}

